import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { orgGetInvitation } from "@/lib/api";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();
  const [state, setState] = useState("loading"); // loading | ready | error
  const [info, setInfo] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("error");
      setErrMsg("Kein Einladungs-Token.");
      return;
    }
    orgGetInvitation(token)
      .then((d) => {
        setInfo(d);
        setState("ready");
      })
      .catch((e) => {
        setErrMsg(e?.response?.data?.detail || "Einladung ungültig oder abgelaufen.");
        setState("error");
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben.");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("Die Passwörter stimmen nicht überein.");
      return;
    }
    setBusy(true);
    try {
      await acceptInvite(token, form.password, form.name.trim());
      toast.success("Willkommen bei BeSca!");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Annahme fehlgeschlagen.");
      setBusy(false);
    }
  };

  if (state === "loading")
    return (
      <AuthShell title="Einladung">
        <div className="flex justify-center py-8" data-testid="invite-loading">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AuthShell>
    );

  if (state === "error")
    return (
      <AuthShell
        title="Einladung ungültig"
        footer={<a href="/login" className="text-primary font-medium hover:underline">Zur Anmeldung</a>}
      >
        <p className="text-sm text-destructive text-center" data-testid="invite-error">{errMsg}</p>
      </AuthShell>
    );

  return (
    <AuthShell
      title="Einladung annehmen"
      subtitle={`${info.inviter_name} hat Sie als ${info.role_label} eingeladen.`}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="accept-invite-form">
        <div className="space-y-2">
          <Label>E-Mail</Label>
          <Input value={info.email} disabled data-testid="invite-email" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            data-testid="invite-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pw">Passwort</Label>
          <Input
            id="pw"
            type="password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            required
            autoComplete="new-password"
            data-testid="invite-password"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cf">Passwort bestätigen</Label>
          <Input
            id="cf"
            type="password"
            value={form.confirm}
            onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
            required
            autoComplete="new-password"
            data-testid="invite-confirm"
          />
        </div>
        <Button type="submit" className="w-full" disabled={busy} data-testid="invite-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Konto aktivieren"}
        </Button>
      </form>
    </AuthShell>
  );
}
